import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

export function initStoreLocator() {
    const mapContainer = document.getElementById('map');
    if (!mapContainer) return;

    const mapboxToken = document.querySelector('[data-mapbox-token]')?.dataset.mapboxToken;
    if (!mapboxToken) {
        console.error('Mapbox token not found');
        return;
    }

    mapboxgl.accessToken = mapboxToken;

    // Initialize map
    const map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-7.6921, 53.1424], // Ireland center
        zoom: 6.5
    });

    // Load stores and setup features
    loadStoresAndInitialize(map);

    return map;
}

async function loadStoresAndInitialize(map) {
    try {
        const response = await fetch('/api/stores.json');

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const stores = await response.json();

        map.on('load', () => {
            // Add stores source with clustering
            map.addSource('stores', {
                type: 'geojson',
                data: stores,
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 50
            });

            // Add cluster circles
            map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'stores',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': '#42414a',
                    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40]
                }
            });

            // Add cluster count labels
            map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'stores',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-size': 12
                },
                paint: { 'text-color': '#ffffff' }
            });

            // Add individual markers
            map.addLayer({
                id: 'unclustered-point',
                type: 'circle',
                source: 'stores',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': '#42414a',
                    'circle-radius': 8,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff'
                }
            });

            // Add geocoder
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                countries: 'ie',
                placeholder: 'Search for a location...'
            });
            document.getElementById('geocoder').appendChild(geocoder.onAdd(map));

            // Initialize filters
            initializeFilters(map, stores);

            // Build sidebar listings
            buildLocationList(stores);

            // Setup click handlers
            setupMapInteractions(map);
        });
    } catch (error) {
        console.error('Error loading stores:', error);
    }
}

function initializeFilters(map, stores) {
    const filterButtons = document.querySelectorAll('[data-filter]');

    filterButtons.forEach(button => {
        button.addEventListener('click', function() {
            filterButtons.forEach(btn => btn.classList.remove('active'));
            this.classList.add('active');

            const filter = this.dataset.filter;
            // Note: Filter by category when categories are added to store data
            updateLocationList(stores, filter);
        });
    });
}

function buildLocationList(stores) {
    const listings = document.getElementById('listings');
    if (!listings) {
        console.error('Listings container not found');
        return;
    }

    listings.innerHTML = ''; // Clear existing

    if (!stores || !stores.features || stores.features.length === 0) {
        listings.innerHTML = '<p style="padding: 1rem;">No stores found.</p>';
        return;
    }

    stores.features.forEach((store, i) => {
        const prop = store.properties;
        const listing = document.createElement('div');
        listing.className = 'listing';
        listing.innerHTML = `
            <h3>${prop.name}</h3>
            <div class="address">${prop.address}</div>
            ${prop.phone ? `<div class="phone"><a href="tel:${prop.phone}">${prop.phone}</a></div>` : ''}
            ${prop.website ? `<a href="${prop.website}" target="_blank" class="website">Visit Website →</a>` : ''}
        `;

        listing.addEventListener('click', () => {
            if (window.storeLocatorMap) {
                window.storeLocatorMap.flyTo({
                    center: store.geometry.coordinates,
                    zoom: 15
                });
                createPopup(store, window.storeLocatorMap);
            }
        });

        listings.appendChild(listing);
    });

}

function updateLocationList(stores, filter) {
    // Update listings based on filter
    // Implementation depends on category structure
    // For now, just rebuild the full list
    buildLocationList(stores);
}

function setupMapInteractions(map) {
    // Click on marker to show popup
    map.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        createPopup(e.features[0], map, coordinates);
    });

    // Hover effect
    map.on('mouseenter', 'unclustered-point', () => {
        map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'unclustered-point', () => {
        map.getCanvas().style.cursor = '';
    });

    // Click on cluster to zoom
    map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0].properties.cluster_id;

        map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
            });
        });
    });

    // Cursor pointer on clusters
    map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
    });
}

function createPopup(store, map, coordinates = null) {
    const coords = coordinates || store.geometry.coordinates;
    const prop = store.properties;

    new mapboxgl.Popup()
        .setLngLat(coords)
        .setHTML(`
            <div class="store-popup">
                <h3>${prop.name}</h3>
                <div class="address">${prop.address}</div>
                ${prop.phone ? `<div class="phone">${prop.phone}</div>` : ''}
                ${prop.website ? `<a href="${prop.website}" target="_blank" class="website">Visit Website</a>` : ''}
            </div>
        `)
        .addTo(map);
}
